import { supabase } from '../../lib/supabase'
import { createTenancy } from './tenancies'
import { toPence } from '../../utils/currency'
import { generateRentSchedule } from '../financials'
import { sendInvite } from '../notifications/invites'
import type { Property } from '../../types/property'
import type { Prospect } from '../../types/prospect'
import {
  TENANT_ONBOARDING_STEPS,
  type TenancyInsert, type DepositScheme, type DepositType, type RentFrequency, type ContractType, type TenancyApprovalStatus,
} from '../../types/tenancy'

/** Onboarding steps serialized for the RPC's p_onboarding_steps jsonb parameter */
const onboardingStepsJsonb = TENANT_ONBOARDING_STEPS.map(s => ({
  step_key: s.key,
  label: s.label,
  description: s.description,
  display_order: s.order,
  is_required: s.required,
}))

export interface CreatedTenantInfo {
  inviteId: string
  tenancyId: string
  name: string
  email: string
  propertyAddress: string
}

export interface SubmitTenancyParams {
  propertyId: string
  unitId: string
  roomId: string
  rentAmount: string
  rentFrequency: RentFrequency
  rentDueDay: string
  startDate: string
  noticePeriodDays: string
  breakClauseDate: string
  breakClauseTerms: string
  depositAmount: string
  depositScheme: DepositScheme | ''
  depositSchemeOther: string
  depositReference: string
  depositProtectedDate: string
  depositType: DepositType | ''
  contractType: ContractType
  guarantorName: string
  guarantorEmail: string
  guarantorPhone: string
  assignedAgentId: string
  tenancyApprovalStatus: TenancyApprovalStatus
  tenantMode: 'existing' | 'new'
  selectedProspectId: string | null
  allProspects: Prospect[]
  newTenantName: string
  newTenantEmail: string
  newTenantPhone: string
  skipOnboarding: boolean
  prospectId?: string | null
  prospect: Prospect | null
  properties: Property[]
}

export interface SubmitTenancyResult {
  tenancyId: string
  inviteInfo?: CreatedTenantInfo
}

export async function submitNewTenancy(params: SubmitTenancyParams): Promise<SubmitTenancyResult> {
  // A room is a unit now (unit_type='room'), so a tenancy names ONE
  // location: the room if one was chosen, otherwise the unit. Migrated rooms
  // kept their id, so params.roomId is already a valid unit id.
  const locationUnitId = params.roomId || params.unitId
  if (!locationUnitId) throw new Error('A unit is required for this property')

  const tenancyData: TenancyInsert = {
    property_id: params.propertyId,
    unit_id: locationUnitId,
    rent_amount_pence: toPence(parseFloat(params.rentAmount)),
    rent_frequency: params.rentFrequency,
    rent_due_day: parseInt(params.rentDueDay),
    start_date: params.startDate,
    end_date: null,
    notice_period_days: parseInt(params.noticePeriodDays),
    break_clause_date: params.breakClauseDate || null,
    break_clause_terms: params.breakClauseTerms || null,
    contract_type: params.contractType,
    rent_review_date: null,
    rent_review_interval: null,
    rent_review_notice_period: null,
    guarantor_name: params.guarantorName || null,
    guarantor_email: params.guarantorEmail || null,
    guarantor_phone: params.guarantorPhone || null,
    assigned_agent_id: params.assignedAgentId || null,
    tenancy_status: params.assignedAgentId ? params.tenancyApprovalStatus : null,
    deposit_amount_pence: params.depositAmount ? toPence(parseFloat(params.depositAmount)) : null,
    deposit_scheme: params.depositScheme || null,
    deposit_scheme_other: params.depositSchemeOther || null,
    deposit_reference: params.depositReference || null,
    deposit_protected_date: params.depositProtectedDate || null,
    deposit_type: params.depositType || null,
    status: params.skipOnboarding
      ? (params.startDate <= new Date().toISOString().split('T')[0] ? 'active' : 'moving_in')
      : 'onboarding',
  }

  const newTenancy = await createTenancy(tenancyData)

  // Only generate rent schedule for active tenancies — onboarding/moving_in
  // tenancies should not have payments generated until they become active
  if (newTenancy.status === 'active') {
    try {
      await generateRentSchedule(newTenancy.id)
    } catch (scheduleErr) {
      // Non-critical - tenancy created, rent schedule can be regenerated
      console.error('Failed to generate rent schedule:', scheduleErr)
    }
  }

  // Handle tenant assignment
  if (params.tenantMode === 'existing' && params.selectedProspectId) {
    const selectedProspect = params.allProspects.find(p => p.id === params.selectedProspectId)
    if (selectedProspect) {
      // Atomic conversion via RPC — RPC handles Path A (registered) vs B (unregistered)
      const { error: convErr } = await supabase.rpc('convert_prospect_to_tenant', {
        p_tenancy_id: newTenancy.id,
        p_prospect_id: params.selectedProspectId,
        p_skip_onboarding: params.skipOnboarding,
        ...(selectedProspect.user_id ? { p_onboarding_steps: onboardingStepsJsonb } : {}),
      })
      if (convErr) throw new Error(`Conversion failed: ${convErr.message}`)

      // A registered prospect (Path A) is notified inside
      // convert_prospect_to_tenant (migration 392). Path B has no account,
      // so its email is still sent here — genuinely non-critical,
      // fire-and-forget.
      if (!selectedProspect.user_id) {
        // The server resolves the address through the prospect's
        // converted_tenancy_id, which the RPC above has just written (PR 2).
        sendInvite('prospect_converted', params.selectedProspectId).catch(() => {})
      }
    }
  } else if (params.tenantMode === 'new' && params.newTenantEmail.trim() && params.newTenantName.trim()) {
    // Path C: new tenant, no prospect — atomic invite creation via RPC
    const { data: pathCResult, error: pathCErr } = await supabase.rpc('convert_prospect_to_tenant', {
      p_tenancy_id: newTenancy.id,
      p_tenant_email: params.newTenantEmail.trim(),
      p_tenant_name: params.newTenantName.trim(),
      p_tenant_phone: params.newTenantPhone.trim() || null,
      p_skip_onboarding: params.skipOnboarding,
    })
    if (pathCErr) throw new Error(`Tenant invite failed: ${pathCErr.message}`)

    const selectedProperty = params.properties.find(p => p.id === params.propertyId)
    const propertyAddress = selectedProperty
      ? `${selectedProperty.address_line1}, ${selectedProperty.city || ''}`
      : 'the property'

    return {
      tenancyId: newTenancy.id,
      inviteInfo: {
        inviteId: pathCResult.invite_id,
        tenancyId: newTenancy.id,
        name: params.newTenantName.trim(),
        email: params.newTenantEmail.trim(),
        propertyAddress
      }
    }
  } else if (params.prospectId && params.prospect) {
    // Atomic conversion via RPC — handles Path A (registered) vs B (unregistered)
    const { error: convErr } = await supabase.rpc('convert_prospect_to_tenant', {
      p_tenancy_id: newTenancy.id,
      p_prospect_id: params.prospectId,
      p_skip_onboarding: params.skipOnboarding,
      ...(params.prospect.user_id ? { p_onboarding_steps: onboardingStepsJsonb } : {}),
    })
    if (convErr) throw new Error(`Conversion failed: ${convErr.message}`)

    // Path A is notified inside convert_prospect_to_tenant (migration 392);
    // Path B has no account, so its email is still sent here.
    if (!params.prospect.user_id) {
      sendInvite('prospect_converted', params.prospectId).catch(() => {})
    }
  }

  // Auto-convert any approved prospect for this property (when no prospect was explicitly selected)
  if (!params.selectedProspectId && !params.prospectId) {
    try {
      const { data: interest } = await supabase
        .from('prospect_property_interests')
        .select('prospect_id')
        .eq('property_id', params.propertyId)
        .limit(1)
        .maybeSingle()

      if (interest?.prospect_id) {
        const { data: prospect } = await supabase
          .from('prospects')
          .select('id, full_name, email, phone, user_id')
          .eq('id', interest.prospect_id)
          .eq('status', 'approved')
          .is('deleted_at', null)
          .maybeSingle()

        if (prospect) {
          // Atomic conversion via RPC — handles A vs B internally
          const { error: autoErr } = await supabase.rpc('convert_prospect_to_tenant', {
            p_tenancy_id: newTenancy.id,
            p_prospect_id: prospect.id,
            p_skip_onboarding: params.skipOnboarding ?? false,
            ...(prospect.user_id ? { p_onboarding_steps: onboardingStepsJsonb } : {}),
          })
          // Auto-convert is best-effort — log but don't throw.
          // This path has never sent a notification. Since migration 392 emits
          // inside the RPC, a registered prospect auto-converted here is now
          // told, like one converted explicitly.
          if (autoErr) console.error('Auto-convert prospect failed:', autoErr.message)
        }
      }
    } catch {
      // Non-critical — prospect auto-link is best-effort
    }
  }

  return { tenancyId: newTenancy.id }
}
